import { Fragment, useState, useEffect, useContext } from 'react';
import { Animate, AccountNav, Card } from 'components/lib';
import { AuthContext } from 'components/lib';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { UMButton } from '@urbanmetrix/um-kit';
import { cancelSubscriptionRenewal, restoreSubscriptionRenewal } from '../../api/billingApi';
import { getCurrentSubscription, getCurrentUsage } from '../../api/planApi';
import type { BillingSubscription, BillingUsage, BillingNextPlan, BillingPlan } from '../../api/planApi';
import { formatBytes } from 'utils/formatBytes';
import { formatCubicMeters } from 'utils/formatCubicMeters';
import { useNavigate } from 'react-router-dom';

import Settings from 'settings.json';
const locale = Settings[import.meta.env.MODE as keyof typeof Settings].locale;

type RenewalDialogAction = 'cancel' | 'restore' | null;

// `planDetails` carries plan limits; model the shapes the view actually reads.
type PlanLimits = NonNullable<BillingPlan['limits']>;

interface LimitRow {
  key: string;
  label: string;
  limit?: number | null;
  used?: number | null;
  type?: 'section';
}

type FormatDateFn = (dateString: string | number | Date) => string;

interface YearlyAmountValue {
  amount?: number | null;
  currency?: string | null;
}

export function Subscription() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<BillingSubscription>(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  const [renewalDialogAction, setRenewalDialogAction] = useState<RenewalDialogAction>(null);
  const [isUpdatingRenewal, setIsUpdatingRenewal] = useState(false);
  const [renewalActionError, setRenewalActionError] = useState('');
  const [activeSection, setActiveSection] = useState('subscription');
  const [usage, setUsage] = useState<BillingUsage>(null);
  const [usageError, setUsageError] = useState('');
  const canManageBilling = authContext.permission?.owner;
  const canViewSubscription = authContext.permission?.admin || authContext.permission?.owner;
  const subscriptionRequestOptions = canManageBilling ? { includeNextPlan: true } : undefined;

  useEffect(() => {
    let mounted = true;

    async function fetchBillingData() {
      try {
        if (mounted) {
          setIsSubscriptionLoading(true);
          setUsageError('');
        }

        const [subscriptionResponse, usageResponse] = await Promise.all([
          getCurrentSubscription(subscriptionRequestOptions),
          getCurrentUsage(),
        ]);

        if (mounted) {
          setSubscription(subscriptionResponse);
          setUsage(usageResponse);
          if (!usageResponse && subscriptionResponse?.status === 'active') {
            setUsageError('Unable to load current usage right now.');
          }
        }
      } catch (error) {
        console.error('Error fetching billing data:', error);
        let resolvedSubscriptionStatus: string | null = null;

        if (mounted) {
          setSubscription(null);
        }

        try {
          const subscriptionResponse = await getCurrentSubscription(subscriptionRequestOptions);
          resolvedSubscriptionStatus = subscriptionResponse?.status || null;

          if (mounted) {
            setSubscription(subscriptionResponse);
          }
        } catch (subscriptionError) {
          console.error('Error fetching subscriptions:', subscriptionError);
          if (mounted) {
            setSubscription(null);
          }
        }

        try {
          const usageResponse = await getCurrentUsage();

          if (mounted) {
            setUsage(usageResponse);
            if (!usageResponse && resolvedSubscriptionStatus === 'active') {
              setUsageError('Unable to load current usage right now.');
            }
          }
        } catch (usageFetchError) {
          console.error('Error fetching billing usage:', usageFetchError);
          if (mounted) {
            setUsage(null);
            if (resolvedSubscriptionStatus === 'active') {
              setUsageError('Unable to load current usage right now.');
            }
          }
        }
      } finally {
        if (mounted) {
          setIsSubscriptionLoading(false);
        }
      }
    }

    fetchBillingData();

    return () => {
      mounted = false;
    };
  }, []);

  async function fetchCurrentSubscription() {
    try {
      setIsSubscriptionLoading(true);
      setUsageError('');

      const [subscriptionResponse, usageResponse] = await Promise.all([
        getCurrentSubscription(subscriptionRequestOptions),
        getCurrentUsage(),
      ]);
      setSubscription(subscriptionResponse);
      setUsage(usageResponse);
      if (!usageResponse && subscriptionResponse?.status === 'active') {
        setUsageError('Unable to load current usage right now.');
      }
    } catch (error) {
      console.error('Error refreshing billing data:', error);
      let resolvedSubscriptionStatus: string | null = null;

      try {
        const subscriptionResponse = await getCurrentSubscription(subscriptionRequestOptions);
        resolvedSubscriptionStatus = subscriptionResponse?.status || null;
        setSubscription(subscriptionResponse);
      } catch (subscriptionError) {
        console.error('Error fetching subscriptions:', subscriptionError);
        setSubscription(null);
      }

      try {
        const usageResponse = await getCurrentUsage();
        setUsage(usageResponse);
        if (!usageResponse && resolvedSubscriptionStatus === 'active') {
          setUsageError('Unable to load current usage right now.');
        }
      } catch (usageFetchError) {
        console.error('Error fetching billing usage:', usageFetchError);
        setUsage(null);
        if (resolvedSubscriptionStatus === 'active') {
          setUsageError('Unable to load current usage right now.');
        }
      }
    } finally {
      setIsSubscriptionLoading(false);
    }
  }

  const formatDate = (dateString: string | number | Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    };
    return new Date(dateString).toLocaleString(locale, options);
  };

  const formatDateOnly = (dateString: string | number | Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return new Date(dateString).toLocaleDateString(locale, options);
  };

  const formatValue = (key: string, value: unknown) => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (key === 'yearlyUnitAmount' || key === 'monthlyUnitAmount') {
      return formatYearlyAmount(value);
    }

    if (key === 'volumeLimit') {
      return formatCubicMeters(Number(value), locale);
    }

    if (
      key.toLowerCase().includes('date') ||
      key.toLowerCase().includes('start') ||
      key.toLowerCase().includes('end') ||
      key === 'cancelAt' ||
      key === 'canceledAt'
    ) {
      return formatDate(value as string | number | Date);
    }

    return String(value);
  };

  const subscriptionFieldLabels: Record<string, string> = {
    productName: 'Product Name',
    volumeLimit: 'Max m³/project',
    sensorPointsLimit: 'Max sensor points/project',
    status: 'Status',
    periodStart: 'Period Start',
    periodEnd: 'Period End',
    startsAt: 'Starts On',
    yearlyUnitAmount: 'Yearly Amount',
    monthlyUnitAmount: 'Monthly Amount',
  };
  const visibleSubscription = subscription && typeof subscription === 'object' ? subscription : null;
  const activeSubscription = visibleSubscription?.status === 'active' ? visibleSubscription : null;
  const currentLimits = visibleSubscription?.planDetails?.limits || null;
  const visibleEntries = activeSubscription
    ? getVisibleSubscriptionEntries(activeSubscription, currentLimits, usage)
    : [];
  const visibleNextPlan = activeSubscription?.nextPlan?.plan ? activeSubscription.nextPlan : null;
  const visibleNextPlanEntries = visibleNextPlan ? getVisibleNextPlanEntries(visibleNextPlan) : [];
  const visibleLimitRows = getVisibleLimitRows(usage);
  const detailRowStyle = {
    display: 'grid',
    gridTemplateColumns: '12rem minmax(0, 1fr)',
    columnGap: '1rem',
    marginBottom: '0.65rem',
    alignItems: 'baseline',
  };
  const renewalDisabled = isRenewalDisabled(activeSubscription);
  const canCancelRenewal = canManageBilling && activeSubscription && !renewalDisabled;
  const canRestoreRenewal = canManageBilling && activeSubscription && renewalDisabled;
  const renewalInfoMessage = getRenewalInfoMessage(activeSubscription, formatDateOnly, visibleNextPlan);
  const planChangeInfoMessage = getPlanChangeInfoMessage(activeSubscription);
  const renewalDialogDescription = getRenewalDialogDescription(renewalDialogAction, activeSubscription, formatDateOnly);
  const hasActiveSubscription = activeSubscription !== null;
  const sections =
    hasActiveSubscription && canViewSubscription
      ? [
          { key: 'subscription', label: 'Subscription' },
          { key: 'limits', label: 'Counters' },
        ]
      : [];

  const openCancelDialog = () => {
    setRenewalActionError('');
    setRenewalDialogAction('cancel');
  };

  const openRestoreDialog = () => {
    setRenewalActionError('');
    setRenewalDialogAction('restore');
  };

  const closeRenewalDialog = () => {
    if (isUpdatingRenewal) return;
    setRenewalDialogAction(null);
  };

  const handleCancelRenewal = async () => {
    setIsUpdatingRenewal(true);
    setRenewalActionError('');

    try {
      const returnUrl = `${window.location.origin}/account/subscription`;
      const response = await cancelSubscriptionRenewal(returnUrl);

      window.location.assign(response.url);
    } catch (error) {
      console.error('Error canceling subscription renewal:', error);
      setRenewalActionError('Unable to start disabling automatic renewal right now.');
      setIsUpdatingRenewal(false);
    }
  };

  const handleRestoreRenewal = async () => {
    setIsUpdatingRenewal(true);
    setRenewalActionError('');

    try {
      await restoreSubscriptionRenewal();
      setRenewalDialogAction(null);
      await fetchCurrentSubscription();
    } catch (error) {
      console.error('Error restoring subscription renewal:', error);
      setRenewalActionError('Unable to enable automatic renewal right now.');
    } finally {
      setIsUpdatingRenewal(false);
    }
  };

  return (
    <Fragment>
      <AccountNav />
      <Animate>
        <div style={{ maxWidth: '64rem', margin: '0 auto' }}>
          {hasActiveSubscription && canViewSubscription ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '13rem minmax(0, 1fr)',
                gap: '0.5rem',
                alignItems: 'start',
              }}
            >
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.18)',
                  borderRadius: '16px',
                  padding: '0.5rem',
                  display: 'grid',
                  gap: '0.25rem',
                }}
              >
                {sections.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => setActiveSection(section.key)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '0.75rem 0.9rem',
                      fontWeight: 700,
                      fontSize: '0.98rem',
                      color: activeSection === section.key ? '#111827' : '#4b5563',
                      background: activeSection === section.key ? 'rgba(255, 255, 255, 0.28)' : 'transparent',
                      boxShadow: 'none',
                      outline: activeSection === section.key ? '1px solid rgba(17, 24, 39, 0.10)' : 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {section.label}
                  </button>
                ))}
              </div>

              <div>
                {activeSection === 'subscription' ? (
                  <Card center extraClass="form-container" isStat={true}>
                    {isSubscriptionLoading ? (
                      <div style={{ minHeight: '14rem', display: 'grid', placeItems: 'center' }}>
                        <CircularProgress size={28} />
                      </div>
                    ) : activeSubscription ? (
                      <>
                        {visibleEntries.map(([key, value]) => (
                          <div key={key} style={detailRowStyle}>
                            <strong style={{ fontWeight: 500 }}>{subscriptionFieldLabels[key] || key}</strong>
                            <span style={{ textAlign: 'left', wordBreak: 'break-word' }}>
                              {formatValue(key, value)}
                            </span>
                          </div>
                        ))}
                        {visibleNextPlan ? (
                          <div
                            style={{
                              marginTop: '1rem',
                              paddingTop: '1rem',
                              borderTop: '1px solid rgba(17, 24, 39, 0.08)',
                            }}
                          >
                            <Typography sx={{ fontWeight: 700, marginBottom: '0.85rem', textAlign: 'left' }}>
                              Next Subscription Update:
                            </Typography>
                            {visibleNextPlanEntries.map(([key, value]) => (
                              <div key={key} style={detailRowStyle}>
                                <strong style={{ fontWeight: 500 }}>{subscriptionFieldLabels[key] || key}</strong>
                                <span style={{ textAlign: 'left', wordBreak: 'break-word' }}>
                                  {formatValue(key, value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : null}
                        <div style={{ marginTop: '1.75rem' }}>
                          {canManageBilling && renewalInfoMessage ? (
                            <Typography sx={{ color: '#4b5563', marginBottom: '0.85rem', textAlign: 'left' }}>
                              {renewalInfoMessage}
                            </Typography>
                          ) : null}
                          {canManageBilling && planChangeInfoMessage ? (
                            <Typography sx={{ color: '#4b5563', marginBottom: '0.85rem', textAlign: 'left' }}>
                              {planChangeInfoMessage}
                            </Typography>
                          ) : null}
                          <div
                            style={{ display: 'flex', justifyContent: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}
                          >
                            {canManageBilling ? (
                              <UMButton
                                text="Update Plan"
                                onClick={() => window.location.assign('/account/change-plan')}
                              />
                            ) : null}
                            {canCancelRenewal ? (
                              <UMButton
                                text="Disable renewal"
                                onClick={openCancelDialog}
                                disabled={isUpdatingRenewal}
                              />
                            ) : null}
                            {canRestoreRenewal ? (
                              <UMButton
                                text="Enable Automatic Renewal"
                                onClick={openRestoreDialog}
                                disabled={isUpdatingRenewal}
                                loading={isUpdatingRenewal}
                              />
                            ) : null}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p>No active subscription found.</p>
                    )}
                  </Card>
                ) : null}

                {activeSection === 'limits' ? (
                  <Card
                    title={usageError ? null : 'Counters of this subscription'}
                    center
                    extraClass="form-container"
                    isStat={true}
                  >
                    {isSubscriptionLoading ? (
                      <div style={{ minHeight: '14rem', display: 'grid', placeItems: 'center' }}>
                        <CircularProgress size={28} />
                      </div>
                    ) : usageError ? (
                      <div
                        className="subscription-usage-error"
                        style={{ color: '#b42318', WebkitTextFillColor: '#b42318' }}
                      >
                        {usageError}
                      </div>
                    ) : visibleLimitRows.length > 0 ? (
                      <>
                        {visibleLimitRows.map((row) => (
                          <div
                            key={row.key}
                            style={
                              row.type === 'section'
                                ? { marginTop: '1.25rem', marginBottom: '0.75rem' }
                                : detailRowStyle
                            }
                          >
                            {row.type === 'section' ? (
                              <strong style={{ fontWeight: 700 }}>{row.label}</strong>
                            ) : (
                              <>
                                <strong style={{ fontWeight: 600 }}>{row.label}</strong>
                                <div
                                  style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}
                                >
                                  {row.used !== null && row.used !== undefined ? (
                                    <span style={getUsageBadgeStyle(row)}>{formatUsageBadgeLabel(row)}</span>
                                  ) : (
                                    <span style={{ textAlign: 'left', wordBreak: 'break-word' }}>
                                      {formatLimitValue(row.key, row.limit)}
                                    </span>
                                  )}
                                  {row.key === 'volume_credits' && canManageBilling ? (
                                    <UMButton
                                      text="Buy Volume"
                                      onClick={() => window.location.assign('/account/buy-volume')}
                                    />
                                  ) : null}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <p>No counters found.</p>
                    )}
                  </Card>
                ) : null}
              </div>
            </div>
          ) : canViewSubscription ? (
            <Card center extraClass="form-container" isStat={true}>
              {isSubscriptionLoading ? (
                <div style={{ minHeight: '14rem', display: 'grid', placeItems: 'center' }}>
                  <CircularProgress size={28} />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.85rem' }}>
                  <p style={{ margin: 0 }}>No active subscription found.</p>
                  {canManageBilling ? (
                    <UMButton
                      text="Subscribe a plan"
                      onClick={() => navigate('/register/plan', { state: { planBackTo: '/account/subscription' } })}
                    />
                  ) : null}
                </div>
              )}
            </Card>
          ) : null}
        </div>
      </Animate>

      <Dialog open={Boolean(renewalDialogAction)} onClose={closeRenewalDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {renewalDialogAction === 'restore' ? 'Enable Automatic Renewal' : 'Disable renewal'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#4b5563', whiteSpace: 'pre-line' }}>{renewalDialogDescription}</Typography>
          {renewalActionError ? (
            <Typography sx={{ color: '#b42318', marginTop: '12px' }}>{renewalActionError}</Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 24px' }}>
          <Button
            variant="contained"
            className="modal-no-btn"
            onClick={closeRenewalDialog}
            disabled={isUpdatingRenewal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="modal-yes-btn"
            onClick={renewalDialogAction === 'restore' ? handleRestoreRenewal : handleCancelRenewal}
            disabled={isUpdatingRenewal}
          >
            {isUpdatingRenewal ? 'Confirming...' : renewalDialogAction === 'cancel' ? 'Continue to Stripe' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

type VisibleSubscription = NonNullable<BillingSubscription>;

function isRenewalDisabled(subscription: VisibleSubscription | null) {
  if (!subscription) return false;
  return subscription.cancelAtPeriodEnd === true || subscription.cancelAt != null;
}

function getVisibleSubscriptionEntries(
  subscription: VisibleSubscription,
  limits: PlanLimits | null,
  usage: BillingUsage
): [string, unknown][] {
  const planDetails = subscription.planDetails;
  const isMonthly = subscription.billingInterval === 'month';
  const price = isMonthly ? planDetails?.trial : planDetails?.yearly;
  const orderedEntries: [string, unknown][] = [
    ['productName', planDetails?.productName],
    ['volumeLimit', getVolumeLimit(limits)],
    ['sensorPointsLimit', getSensorPointsLimit(usage, limits)],
    ['status', subscription.status],
    ['periodStart', subscription.periodStart],
    ['periodEnd', subscription.periodEnd],
    [
      isMonthly ? 'monthlyUnitAmount' : 'yearlyUnitAmount',
      price?.unitAmount !== null && price?.unitAmount !== undefined
        ? { amount: price.unitAmount, currency: price.currency }
        : null,
    ],
  ];

  return orderedEntries.filter(([, value]) => value !== null && value !== undefined);
}

function getVisibleNextPlanEntries(nextPlan: NonNullable<BillingNextPlan>): [string, unknown][] {
  const plan = nextPlan?.plan;
  const yearlyAmount = plan?.yearly?.unitAmount;
  const yearlyCurrency = plan?.yearly?.currency;
  const orderedEntries: [string, unknown][] = [
    ['productName', plan?.productName],
    ['volumeLimit', getVolumeLimit(plan?.limits)],
    ['sensorPointsLimit', getSensorPointsLimit(null, plan?.limits ?? null)],
    ['startsAt', nextPlan?.startsAt],
    [
      'yearlyUnitAmount',
      yearlyAmount !== null && yearlyAmount !== undefined ? { amount: yearlyAmount, currency: yearlyCurrency } : null,
    ],
  ];

  return orderedEntries.filter(([, value]) => value !== null && value !== undefined);
}

function getVolumeLimit(limits?: (PlanLimits & { volume?: number | string | null }) | null) {
  const volume = limits?.volume;
  if (volume === null || volume === undefined) return null;
  const numericVolume = Number(volume);
  return Number.isNaN(numericVolume) ? null : numericVolume;
}

function getSensorPointsLimit(usage: BillingUsage | null, limits: PlanLimits | null) {
  const sensorPointsLimit = usage?.sensorPointsLimit ?? limits?.sensor_points;
  if (sensorPointsLimit === null || sensorPointsLimit === undefined) return null;
  const numericSensorPointsLimit = Number(sensorPointsLimit);
  return Number.isNaN(numericSensorPointsLimit) ? null : numericSensorPointsLimit;
}

function formatLimitValue(key: string, value: unknown) {
  if (value === null || value === undefined) return '-';
  if (key === 'storage_bytes') {
    return formatBytes(Number(value), locale);
  }
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return number.toLocaleString(locale);
}

function getVisibleLimitRows(usage: BillingUsage): LimitRow[] {
  const rows: LimitRow[] = [
    {
      key: 'volume_credits',
      label: 'Volume',
      used: usage?.volumeCredits ?? null,
    },
    {
      key: 'users',
      label: 'Users',
      limit: usage?.usersLimit,
      used: usage?.usersUsed ?? null,
    },
    {
      key: 'storage_bytes',
      label: 'Storage',
      limit: usage?.storageLimitBytes,
      used: usage?.storageUsedBytes ?? null,
    },
  ];

  return rows.filter((row) =>
    row.key === 'volume_credits'
      ? row.used !== null && row.used !== undefined
      : row.limit !== null && row.limit !== undefined
  );
}

function formatUsageBadgeLabel(row: LimitRow) {
  if (row.key === 'volume_credits') {
    return `${formatCubicMeters(Number(row.used), locale)} m³ available`;
  }
  return `${formatLimitValue(row.key, row.used)} used / ${formatLimitValue(row.key, row.limit)}`;
}

function getUsageBadgeStyle(row: LimitRow): React.CSSProperties {
  const numericUsed = Number(row?.used);
  const numericLimit = Number(row?.limit);
  const isLimitReached =
    row.key !== 'volume_credits' &&
    !Number.isNaN(numericUsed) &&
    !Number.isNaN(numericLimit) &&
    numericUsed >= numericLimit;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.24rem 0.6rem',
    borderRadius: '999px',
    background: !isLimitReached ? 'rgba(34, 197, 94, 0.14)' : 'rgba(236, 72, 153, 0.12)',
    color: !isLimitReached ? '#15803d' : '#be185d',
    fontWeight: 700,
    fontSize: '0.85rem',
    lineHeight: 1,
  };
}

function formatCurrencyAmount(value: unknown) {
  if (value === null || value === undefined) return '-';
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return (number / 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatYearlyAmount(value: unknown) {
  if (value === null || value === undefined) return '-';
  if (typeof value !== 'object') return formatCurrencyAmount(value);

  const yearly = value as YearlyAmountValue;
  const amount = formatCurrencyAmount(yearly.amount);
  const currency = typeof yearly.currency === 'string' && yearly.currency ? yearly.currency.toLowerCase() : '';

  return currency ? `${amount} ${currency}` : amount;
}

function getRenewalInfoMessage(
  subscription: VisibleSubscription | null,
  formatDate: FormatDateFn,
  nextPlan: NonNullable<BillingNextPlan> | null
) {
  if (!subscription) return '';

  if (nextPlan?.startsAt) {
    return `This scheduled plan update will become active on ${formatDate(nextPlan.startsAt)}.`;
  }

  if (!isRenewalDisabled(subscription)) {
    if (!subscription.periodEnd)
      return 'Your subscription will renew automatically at the end of the current billing period.';
    return `Your subscription will renew automatically on ${formatDate(subscription.periodEnd)}.`;
  }

  if (!subscription.periodEnd) {
    return 'Automatic renewal has been disabled. Your subscription will remain active until the end of the current billing period.';
  }

  return `Automatic renewal has been disabled. Your subscription will remain active until ${formatDate(subscription.periodEnd)}.`;
}

function getPlanChangeInfoMessage(subscription: VisibleSubscription | null) {
  if (!subscription) return '';

  if (!isRenewalDisabled(subscription)) {
    return 'Here you can disable automatic renewal for your subscription or update your plan. Updates to a higher tier take effect immediately, while updates to a lower tier apply at the end of the current billing period.';
  }

  return 'Here you can enable automatic renewal for your subscription or update your plan. Updates to a higher tier take effect immediately, while updates to a lower tier apply at the end of the current billing period. Please note that updating your plan will reactivate annual renewal.';
}

function getRenewalDialogDescription(
  action: RenewalDialogAction,
  subscription: VisibleSubscription | null,
  formatDate: FormatDateFn
) {
  const renewalAmount = subscription?.planDetails?.yearly?.unitAmount;
  const renewalCurrency = subscription?.planDetails?.yearly?.currency;
  const formattedRenewalAmount =
    renewalAmount !== null && renewalAmount !== undefined
      ? formatYearlyAmount({ amount: renewalAmount, currency: renewalCurrency })
      : null;
  const renewalCostMessage = formattedRenewalAmount ? ` The renewal cost will be ${formattedRenewalAmount}.` : '';

  if (action === 'restore') {
    if (!subscription?.periodEnd) {
      return `Are you sure you want to enable automatic renewal for this subscription?\n\nAutomatic renewal will be enabled again at the end of the current billing period.${renewalCostMessage}`;
    }

    return `Are you sure you want to enable automatic renewal for this subscription?\n\nIf you confirm, the subscription will renew automatically on ${formatDate(subscription.periodEnd)}.${renewalCostMessage}`;
  }

  if (!subscription?.periodEnd) {
    return 'To disable automatic renewal, you will continue to Stripe to complete this change.\n\nYour subscription will remain active until the end of the current billing period.';
  }

  return `To disable automatic renewal, you will continue to Stripe to complete this change.\n\nYour subscription will remain active until ${formatDate(subscription.periodEnd)} and will not renew automatically after that date.`;
}
